import { SSO_AUTH_MODE } from '@core/runtime-config'
import { isPublicSsoPath } from '@core/sso'
import { useGetMe } from '@shared/api/users'
import { ErrorState } from '@shared/components/feedback/ErrorState'
import { LoadingState } from '@shared/components/feedback/LoadingState'
import { isAxiosError } from 'axios'
import { type ReactNode, useEffect } from 'react'
import { useAuthStore } from '../hooks/useAuthStore'
import { mapAuthenticatedUser } from '../service/auth.service'

export function AuthSessionBoundary({ children }: { children: ReactNode }) {
  const sessionEnded = useAuthStore((state) => state.sessionEnded)
  const { data, isPending, isError, error, refetch } = useGetMe({
    query: {
      enabled: !sessionEnded,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  })
  const setUser = useAuthStore((state) => state.setUser)
  const logout = useAuthStore((state) => state.logout)
  const authenticatedUser = useAuthStore((state) => state.user)
  const remoteUser = data?.data
  const status = isAxiosError(error) ? error.response?.status : undefined
  const authenticationFailed = isError && (status === 401 || status === 403)

  useEffect(() => {
    if (sessionEnded) {
      return
    }

    if (authenticationFailed) {
      logout()
      return
    }

    if (remoteUser) {
      setUser(mapAuthenticatedUser(remoteUser))
      return
    }
  }, [authenticationFailed, logout, remoteUser, sessionEnded, setUser])

  if (sessionEnded) {
    return children
  }

  if (isError && !authenticationFailed) {
    // As páginas públicas de SSO (login e continuação do convite) apresentam suas próprias falhas.
    if (SSO_AUTH_MODE && isPublicSsoPath(window.location.pathname)) return children

    return (
      <ErrorState
        title="Não foi possível restaurar a sessão"
        message="Verifique sua conexão e tente novamente. Sua sessão foi preservada."
        retry={() => {
          void refetch()
        }}
      />
    )
  }

  if (
    isPending ||
    (!authenticationFailed && remoteUser && authenticatedUser?.id !== remoteUser.id)
  ) {
    return <LoadingState message="Restaurando sessão..." />
  }

  return children
}
